import axios from 'axios'
import { Api } from './api'
import { uuidv7 } from 'uuidv7'

export const getRandomRef = () => uuidv7()

export type PreSignOutput = {
  url: string
  path: string
  acl: Acl
  location: string
  fields: Record<string, string>
}

export enum Acl {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE'
}

export enum StorageItemRefType {
  STORE_AVATAR = 'STORE_AVATAR',
  BOT_ATTACHMENT = 'BOT_ATTACHMENT',
  MESSAGE_ATTACHMENT = 'MESSAGE_ATTACHMENT',
  USER_PROFILE_PICTURE = 'USER_PROFILE_PICTURE',
  CLIENT_PROFILE_PICTURE = 'CLIENT_PROFILE_PICTURE',
}

export const StorageItemRefTypeDict = {
  [StorageItemRefType.STORE_AVATAR]: 'Avatar da Loja',
  [StorageItemRefType.BOT_ATTACHMENT]: 'Anexos do BOT',
  [StorageItemRefType.MESSAGE_ATTACHMENT]: 'Anexos Atendimento',
  [StorageItemRefType.USER_PROFILE_PICTURE]: 'Imagem do usuário',
  [StorageItemRefType.CLIENT_PROFILE_PICTURE]: 'Imagem contato'
}

type AssignedOutput = {
  form: FormData
  location: string
  url: string,
}

export type GetPreAssignUrlProps = {
  file: File,
  location: string,
  refId: string,
  storeId: number
  refType: StorageItemRefType,
  mimeType: string
  originalFileName: string,
  onUploadProgress?: (progress: number) => void
}

const getPreAssignUrl = async (props: GetPreAssignUrlProps): Promise<AssignedOutput> => {
  const { file, location, storeId, refId, refType, mimeType, originalFileName } = props
  const { data } = await Api.post<PreSignOutput>(`${process.env.REACT_APP_API_V2}/storage/file/pre-sign`, {
    location, refId, refType, mimeType, originalFileName
  })
  if (!data) throw new Error('Ocorreu um erro ao realizar a requisição')

  const form = new FormData()

  for (const [key, value] of Object.entries(data.fields)) {
    form.append(key, value)
  }

  const acl = {
    [Acl.PRIVATE]: 'private',
    [Acl.PUBLIC]: 'public-read'
  }[data.acl]

  const tags = [
    { key: 'keep', value: 'false' },
    { key: 'refId', value: refId },
    { key: 'refType', value: refType },
    { key: 'storeId', value: String(storeId) }
  ]

  const tagsString = tags.map(tag => `<Tag><Key>${tag.key}</Key><Value>${tag.value}</Value></Tag>`).join('')

  form.append('acl', acl)
  form.append('Content-Type', file.type)
  form.append('tagging', `<Tagging><TagSet>${tagsString}</TagSet></Tagging>`)

  // Add metadata
  form.append('x-amz-meta-acl', acl)
  form.append('x-amz-meta-storeId', String(storeId))
  form.append('x-amz-meta-refId', refId)
  form.append('x-amz-meta-refType', refType)
  form.append('x-amz-meta-originalFileName', originalFileName)

  form.append('file', file)

  return {
    form,
    location: data.location,
    url: data.url
  }
}

export const uploadFile = async (props: GetPreAssignUrlProps): Promise<string> => {
  const { form, location, url } = await getPreAssignUrl(props)

  try {
    // Create a clean axios instance without any default headers/auth
    const cleanAxios = axios.create({
      timeout: 60000,
      withCredentials: false
    })

    await cleanAxios.request({
      url,
      data: form,
      method: 'post',
      headers: {}, // Explicitly empty headers
      onUploadProgress: p => {
        if (!p.total) return
        const percentage = Math.round((p.loaded * 100) / p.total)
        props.onUploadProgress?.(percentage)
      }
    })

    return location
  } catch (error) {
    console.error(error)
    throw new Error('Ocorreu um erro ao realizar o upload do arquivo')
  }
}

export type AttachmentUploadOutput = {
  type: MessageAttachmentType;
  key: string;
  originalname: string;
};

export enum MessageAttachmentType {
  FILE = 'FILE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  PICTURE = 'PICTURE',
  CONTACT = 'CONTACT',
  LOCATION = 'LOCATION'
}
